import { Suspense, useRef, useState } from 'react';
import { array } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { TextKey } from '@mews-commander/enums';
import { lt, useSafeUrlState } from '@mews/framework';
import { WithGuideLink } from '@mews-commander/types';
import { Tab } from '@optimus-web/core';
import { OverlayedLoader } from '@mews-commander/ui';
import { FilterSidebarLayoutBase } from '../../components/layout';
import { ReportTab, activeTabUrlSchema } from './schema';
import {
    ReservationsFilterSidebarLayoutContent,
    ReservationsFilterValues,
    ResourceBlocksFilterSidebarLayoutContent,
    ResourceBlocksFilterValues,
} from './components';
import { useHeadingActions } from './hooks';

const tabs = pipe(
    [
        { textKey: TextKey.GuestInHouseReportTabReservations, value: ReportTab.Reservations },
        { textKey: TextKey.GuestInHouseReportTabSpaceBlocks, value: ReportTab.SpaceBlocks },
    ],
    array.map(({ textKey, value }) => <Tab key={value} id={value} label={lt(textKey)} value={value} />)
);

export const GuestsInHouseReport = ({ guideLink }: WithGuideLink) => {
    const [urlState, setUrlState] = useSafeUrlState(activeTabUrlSchema);
    const [activeTab, setActiveTab] = useState(urlState?.activeTabId || ReportTab.Reservations);

    const reservationsfilterValuesRef = useRef<ReservationsFilterValues | undefined>();
    const resourceBlocksfilterValuesRef = useRef<ResourceBlocksFilterValues | undefined>();

    const saveReservationsLastFilterValues = (values: ReservationsFilterValues) => {
        reservationsfilterValuesRef.current = values;
    };
    const saveResourceBlocksLastFilterValues = (values: ResourceBlocksFilterValues) => {
        resourceBlocksfilterValuesRef.current = values;
    };

    const handleTabClick = (tabId: ReportTab) => {
        const other =
            tabId === ReportTab.Reservations
                ? reservationsfilterValuesRef.current
                : resourceBlocksfilterValuesRef.current;

        setActiveTab(tabId);
        setUrlState({ activeTabId: tabId, ...other });
    };

    const actions = useHeadingActions();

    return (
        <FilterSidebarLayoutBase
            title={lt('GuestInHouseReport')}
            actions={actions}
            withToggleFilterHeaderAction
            tabsConfig={{
                activeTabValue: activeTab,
                handleTabClick: (_e, tab) => handleTabClick(tab as ReportTab),
                inline: true,
                tabs,
            }}
            guideLink={guideLink}
        >
            {(sidebarProps) => (
                <Suspense fallback={<OverlayedLoader show />}>
                    {activeTab === ReportTab.Reservations ? (
                        <ReservationsFilterSidebarLayoutContent
                            {...sidebarProps}
                            saveValues={saveReservationsLastFilterValues}
                            defaultQueryParams={{ activeTabId: ReportTab.Reservations }}
                        />
                    ) : (
                        <ResourceBlocksFilterSidebarLayoutContent
                            {...sidebarProps}
                            saveValues={saveResourceBlocksLastFilterValues}
                            defaultQueryParams={{ activeTabId: ReportTab.SpaceBlocks }}
                        />
                    )}
                </Suspense>
            )}
        </FilterSidebarLayoutBase>
    );
};
